<script lang="ts">
  import Data from "../../components/Data.svelte";
  import { formatNumber } from "../../helpers/string";
  import { ethereum } from "../../store/ethereum";
  import { getContext } from "svelte";
  import ConnectModal from "../../components/modals/ConnectModal.svelte";
  import ErrorModal from "../../components/modals/ErrorModal.svelte";
  import { formatEther, parseEther } from "@ethersproject/units";
  import TransactionModal from "../../components/modals/TransactionModal.svelte";
  import type { Signer } from "@ethersproject/abstract-signer";
  import type { BigNumber } from "@ethersproject/bignumber";
  import type { Contract } from "@ethersproject/contracts";
  import StakeModal from "../../components/modals/StakeModal.svelte";

  const { open, close } = getContext("simple-modal");

  export let baseToken: Contract;
  export let rewardToken: Contract;
  export let stakingContract: Contract;

  export let baseSymbol: string
  export let rewardSymbol: string

  export let basePrice: number;
  export let rewardPrice: number;

  // staking apr formular
  // ((86400 * 5.015432098765432098) * 365) / 35173041.26120 * 100
  // ((seconds_day * reward_rate) * days_year) / totalStaked * 100
  // (seconds_day * reward_rate * days_year * reward_price) /
  // totalStaked * base_price * 100

  let baseBalanceRaw: any;
  let baseBalance = 0;
  let baseAmount = 0;
  let baseStaked = 0;
  let rewardEarned = 0;
  let totalbaseStaked = 0;
  let rewardRate = 0;
  $: connected = $ethereum.signer != undefined;
  $: getBalances($ethereum.signer, basePrice, rewardPrice);
  let loading = false;
  let approved = false;

  const delay = (sec: number) => {
    return new Promise<void>((resolve) => {
      setTimeout(resolve, 1000 * sec);
    });
  };

  const getBalances = async (s: Signer, basePrice: number, rewardPrice: number) => {
    if (s == null) return;
    if (basePrice == 0 || rewardPrice == 0) {
      console.log("prices", basePrice, rewardPrice)
      return;
    }

    console.log("fetching info")

    try {
      const _baseBalanceRaw = await baseToken
        .connect($ethereum.signer)
        .balanceOf($ethereum.address);
      baseBalanceRaw = _baseBalanceRaw;
      baseBalance = parseFloat(formatEther(_baseBalanceRaw));
    } catch (err) {
      baseBalance = 0;
    }

    await delay(0.1);

    try {
      const _baseStaked = await stakingContract
        .connect($ethereum.signer)
        .balanceOf($ethereum.address);
      baseStaked = parseFloat(formatEther(_baseStaked));
    } catch (err) {
      baseStaked = 0;
    }

    await delay(0.1);

    try {
      const _rewardEarned = await stakingContract
        .connect($ethereum.signer)
        .earned($ethereum.address);
      rewardEarned = parseFloat(formatEther(_rewardEarned));
    } catch (err) {
      rewardEarned = 0;
    }

    await delay(0.1);

    try {
      const _approved: BigNumber = await baseToken
        .connect($ethereum.signer)
        .allowance($ethereum.address, stakingContract.address);
      const amountApproved = parseFloat(formatEther(_approved));
      approved = amountApproved >= baseBalance;
      if (_approved.isZero()) approved = false;
      console.log("Approved", _approved.toString())
    } catch (err) {
      console.error(err)
      baseBalance = 0;
    }

    await delay(0.1);

    try {
      const _totalStaked = await stakingContract
        .connect($ethereum.signer)
        .totalSupply();
      const _rewardRate = await stakingContract
        .connect($ethereum.signer)
        .rewardRate();
      rewardRate = parseFloat(formatEther(_rewardRate));
      totalbaseStaked = parseFloat(formatEther(_totalStaked));
    } catch (err) {
      rewardRate = 0;
      totalbaseStaked = 0;
    }

    baseAmount = Math.floor(baseBalance);
  };

  const awaitTx = async (res: any, msg: string) => {
    open(TransactionModal, {
      message: msg,
      details: `TxHash: ${res.hash}`,
      hash: res.hash,
    });
    await res.wait();
    close();
    return;
  };

  const handleTxError = (err: any) => {
    try {
      close();
    } catch {
      console.log("nothing to close");
    }

    let msg = err.toString();
    if (err.message && err.code)
      msg = `code: ${err.code}\nmessage: ${err.message}`;

    open(ErrorModal, { message: "Transaction Failed", details: msg });
    console.error(err);
  };

  const approve = async () => {
    if (!connected) return connectFirst();
    loading = true;

    try {
      const _stakingContract = stakingContract;
      const res = await baseToken
        .connect($ethereum.signer)
        .approve(_stakingContract.address, parseEther("1000000000000000000"));

      await awaitTx(res, "Approving $DOE for staking. Remember you must still stake after this. please wait...");
      getBalances($ethereum.signer, basePrice, rewardPrice);
    } catch (err) {
      handleTxError(err);
    }

    loading = false;
  };

  const stake = async () => {
    if (!connected) return connectFirst();
    loading = true;

    try {
      let _value = parseEther(baseAmount.toString());
      _value = _value.gt(baseBalanceRaw) ? baseBalanceRaw : _value;

      const gas = await stakingContract
        .connect($ethereum.signer)
        .estimateGas.stake(_value);

      console.log(gas.toNumber());
      const gasLimit = Math.max(gas.toNumber() + 5000, 110159);

      const res = await stakingContract
        .connect($ethereum.signer)
        .stake(_value, {
          gasLimit: gasLimit.toString(),
        });

      await awaitTx(res, `Staking ${baseAmount} $DOE, please wait...`);
      setTimeout(() => getBalances($ethereum.signer, basePrice, rewardPrice), 1000);
    } catch (err) {
      handleTxError(err);
    }

    loading = false;
  };

  const claimAndExit = async () => {
    if (!connected) return connectFirst();
    loading = true;

    try {
      const gas = await stakingContract
        .connect($ethereum.signer)
        .estimateGas.exit();

      console.log(gas.toNumber());
      const gasLimit = Math.max(gas.toNumber() + 5000, 100000);

      const res = await stakingContract
        .connect($ethereum.signer)
        .exit({ gasLimit: gasLimit.toString() });

      await awaitTx(res, `Exiting $DOE staking pool, please wait...`);
      getBalances($ethereum.signer, basePrice, rewardPrice);
    } catch (err) {
      handleTxError(err);
    }

    loading = false;
  };

  const connectFirst = () => {
    open(ConnectModal, {
      message:
        "You must connect your wallet before claiming. Set you wallet's network to Ethereum.",
    });
  };

  const updateAmount = (e: any) => {
    if (e.target) {
      const _v = e.target.value;
      if (_v < 0) {
        baseAmount = 0;
        e.target.value = baseAmount;
        return false;
      }

      if (_v > baseBalance) {
        baseAmount = baseBalance;
        e.target.value = baseAmount;
        return true;
      }

      baseAmount = _v;
      return;
    }
  };

  $: apr = (86400 * rewardRate * 365 * 100 * rewardPrice) / (totalbaseStaked * basePrice)

</script>
<div class="col-lg-4 col-md-6 col-12">
  <div class="stakig_card_top">
    <img src="/img/images/{baseSymbol.toLowerCase()}.svg" alt="icon"/>
    <img src="/img/images/{rewardSymbol.toLowerCase()}.svg" alt="icon"/>
    <p>{rewardSymbol} Staking</p>
  </div>
  <div class="stakig_card_bottom">
    <div class="row">
      <div class="col-6">
        <span>APR</span>
        <p>{formatNumber(apr, 2)}%</p>
      </div>
      <div class="col-6">
        <span>Wallet Balance</span>
        <p>{formatNumber(baseBalance, 1)} {baseSymbol}</p>
      </div>
      <div class="col-6">
        <span>Staked</span>
        <p>{formatNumber(baseStaked, 1)} {baseSymbol}</p>
      </div>
      <div class="col-6">
        <span>Earned</span>
        <p>{formatNumber(rewardEarned, 4)} {rewardSymbol}</p>
      </div>
    </div>
    {#if rewardSymbol == "DOE Old"}
    <div class="row">
      <div class="col-6">
        <span>APR</span>
        <p>{formatNumber(apr, 2)}%</p>
      </div>
      <div class="col-6">
        <span>Wallet Balance</span>
        <p>{formatNumber(baseBalance, 1)} {baseSymbol}</p>
      </div>
      <div class="col-6">
        <span>Staked</span>
        <p>{formatNumber(baseStaked, 1)} {baseSymbol}</p>
      </div>
      <div class="col-6">
        <span>Earned</span>
        <p>{formatNumber(rewardEarned, 4)} {rewardSymbol}</p>
      </div>
    </div>
    <div class="stake-warning">
      <h2>
        <i class="fas fa-exclamation-triangle" />
        Caution: <br/>
        Staking for this contract has ended.
      </h2>
      <button class="btn btn-primary stake-warning-button" on:click={() => open(StakeModal, {})}> Learn More </button>
    </div>
    {/if}
    {#if rewardSymbol == "SHIB"}
    <div class="stake-warning">
      <h2>
        <i class="fas fa-exclamation-triangle" />
        Caution: <br/>
        SHIB Staking is no longer earning SHIB rewards.
      </h2>
      <h2>You will still be able to unstake and claim your SHIB rewards if you have DOE staked in this pool.</h2>
    </div>
    {:else}
      <div class="name_amount_cards">
        <label>Amount to Stake (max: {baseBalance})</label>
        <div>
          <input
          type="number"
          value={baseAmount}
          max={`${baseBalance}`}
          on:input={updateAmount}
        />  
        </div>
      </div>
      {/if}
    {#if !connected}
    <button class="connect_common_btn" on:click={connectFirst}>
      Connect Wallet
    </button>
    {:else}
      {#if rewardSymbol == "SHIB" || rewardSymbol == "DOE Old"}
      <button
      class="connect_common_btn"
      disabled={loading || true}
    >
      Stake (Disabled)
    </button>
      {:else}
        {#if !approved}
          <button
            class="connect_common_btn"
            on:click={approve}
            disabled={loading}
          >
          Approval Required {baseSymbol}
          </button>
        {:else}
          <button
            class="connect_common_btn"
            on:click={stake}
            disabled={loading}
          >
            Stake {formatNumber(baseAmount, 2)} {baseSymbol}
          </button>
        {/if}
      {/if}
        <button
          class="connect_common_btn red_btn"
          on:click={claimAndExit}
          disabled={loading}
        >
          Claim and Unstake
        </button>      
      {/if}
  </div>
</div>

<style>
.stake-warning {
  padding: 10px;
  color: black;
  background-color: #FAB747;
  border-radius: 10px;
  border: 1px solid rgb(184, 135, 0);
  margin: 20px auto;
  text-align: center;
}

.stake-warning-button {
  color: black;
  background-color: rgb(184, 135, 0);
}

.stake-warning h2 {
  font-size: 1rem;
}
</style>
